import { isValidPhoneNumber, parsePhoneNumberWithError } from 'libphonenumber-js'
import { type z } from 'zod'

import { type schemas } from '~interface/zod-schema'

export type PhoneNumber0 = z.infer<typeof schemas.PhoneNumber0>

export const phoneComponents = (ph: string): [string, string] => {
  const [phone, extension = ''] = ph.split('x')
  return [phone.replace(/\D/g, ''), extension.replace(/\D/g, '')]
}

export const phoneWithExtension = (phone?: string | null, extension?: number | null): string => {
  const [number] = phoneComponents(phone ?? '')
  const national = number.length === 11 && number.startsWith('1') ? number.slice(1) : number
  return national && extension != null ? `${national}x${extension}` : national
}

export const phoneValid = (phone: string): boolean => {
  // Validate that a valid US phone number is provided
  if (!phone || phone.trim().length === 0 || phone.replace(/\D/g, '').length > 10) {
    return false
  }
  try {
    if (!isValidPhoneNumber(phone, 'US')) {
      return false
    }
    const parsedNumber = parsePhoneNumberWithError(phone, 'US')
    if (!parsedNumber || parsedNumber.country !== 'US') {
      return false
    }
    return true
  } catch (_) {
    return false
  }
}

export const usPhoneValid = (phone: string): boolean => {
  // Validate a US/NANP phone number, accepting both local (2125550123) and
  // country-code / E.164 (+12125550123) forms. Unlike phoneValid, this does
  // not impose a 10-digit limit, so it mirrors the backend validator
  // (country code 1 + is_valid_number) and handles values exported from the
  // app (which are stored in E.164).
  if (!phone || phone.trim().length === 0) {
    return false
  }
  try {
    if (!isValidPhoneNumber(phone, 'US')) {
      return false
    }
    const parsedNumber = parsePhoneNumberWithError(phone, 'US')
    return !!parsedNumber && parsedNumber.countryCallingCode === '1'
  } catch (_) {
    return false
  }
}
